import { useMemo, useState } from 'react'
import type { DateRange } from 'react-day-picker'
import { CalendarBlank } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/cn'
import { formatIST } from '@/lib/time'
import {
  MAX_RANGE_DAYS,
  addDaysIso,
  clampCustomRange,
  parseIsoDate,
  todayIso,
  type DateFilter,
  type DatePreset,
} from './date-range'

const PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: '90d', label: '90d' },
]

const PILL =
  'inline-flex h-7 items-center justify-center rounded-sm px-2.5 text-[11.5px] font-medium whitespace-nowrap transition-colors duration-[130ms] max-md:min-h-11 max-md:flex-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--ring)]'
const PILL_ON = 'bg-[color:var(--primary)] text-[color:var(--primary-foreground)]'
const PILL_OFF =
  'text-[color:var(--muted-foreground)] hover:bg-[color:var(--muted)] hover:text-[color:var(--foreground)]'

/**
 * Segmented control — All | 7d | 30d | 90d | Custom.
 *
 * Custom opens a popover with a two-month range calendar (plus typed From /
 * To fields for keyboard entry). A custom range cannot span more than 180
 * days: the calendar disables dates outside the window the current `from`
 * allows, and the inputs clamp the same way.
 */
export function DateFilterControl({
  value,
  onChange,
  className,
}: {
  value: DateFilter
  onChange: (next: DateFilter) => void
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const today = todayIso()
  const isCustom = value.preset === 'custom'

  const setFrom = (from: string) => {
    const next = clampCustomRange(from, value.to)
    onChange({ preset: 'custom', from: next.from, to: next.to })
  }

  const setTo = (to: string) => {
    const next = clampCustomRange(value.from, to)
    onChange({ preset: 'custom', from: next.from, to: next.to })
  }

  const selected = useMemo<DateRange | undefined>(() => {
    if (!isCustom) return undefined
    const from = value.from ? parseIsoDate(value.from) : null
    const to = value.to ? parseIsoDate(value.to) : null
    if (!from) return undefined
    return { from, to: to ?? undefined }
  }, [isCustom, value.from, value.to])

  const onSelectRange = (range: DateRange | undefined) => {
    if (!range?.from) {
      onChange({ preset: 'custom' })
      return
    }
    const from = toIso(range.from)
    // react-day-picker hands back `to === from` on the first click of a range;
    // treat that as an open range so the second click can set the far end.
    const to = range.to && range.to.getTime() !== range.from.getTime() ? toIso(range.to) : undefined
    const next = clampCustomRange(from, to)
    onChange({ preset: 'custom', from: next.from, to: next.to })
  }

  const disabledDays = useMemo(() => {
    const todayDate = parseIsoDate(today)
    const rules: Array<{ after: Date } | { before: Date }> = []
    if (todayDate) rules.push({ after: todayDate })
    return rules
  }, [today])

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <span
        className="inline-flex items-center gap-0.5 rounded-md bg-[color:var(--card)] p-0.5 shadow-[var(--shadow-raised)] max-md:flex max-md:w-full"
        role="group"
        aria-label="Filter purchase orders by PO date"
      >
        {PRESETS.map(preset => (
          <button
            key={preset.key}
            type="button"
            onClick={() => onChange({ preset: preset.key })}
            className={cn(PILL, value.preset === preset.key ? PILL_ON : PILL_OFF)}
            aria-pressed={value.preset === preset.key}
          >
            {preset.label}
          </button>
        ))}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            type="button"
            onClick={() => {
              if (!isCustom) onChange({ preset: 'custom', from: value.from, to: value.to })
            }}
            className={cn(PILL, 'gap-1.5', isCustom ? PILL_ON : PILL_OFF)}
            aria-pressed={isCustom}
            aria-label="Custom"
          >
            <CalendarBlank className="size-3.5" />
            {isCustom ? rangeLabel(value.from, value.to) : 'Custom'}
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-3">
            <div className="flex flex-col gap-3">
              <Calendar
                mode="range"
                numberOfMonths={2}
                defaultMonth={selected?.from ?? parseIsoDate(today) ?? undefined}
                selected={selected}
                onSelect={onSelectRange}
                disabled={disabledDays}
                max={MAX_RANGE_DAYS}
                // A blank picker is confusing; land on today's month.
                required={false}
              />
              <div className="flex flex-wrap items-end gap-2 border-t border-[color:var(--border)] pt-3">
                <label className="flex flex-col gap-1 text-[10.5px] font-semibold uppercase tracking-wider text-[color:var(--muted-foreground)]">
                  From
                  <Input
                    type="date"
                    value={value.from ?? ''}
                    max={value.to || today}
                    onChange={event => setFrom(event.target.value)}
                    className="h-8 w-[10.5rem] px-2 text-[12px] font-normal normal-case tracking-normal"
                    aria-label="From date"
                  />
                </label>
                <label className="flex flex-col gap-1 text-[10.5px] font-semibold uppercase tracking-wider text-[color:var(--muted-foreground)]">
                  To
                  <Input
                    type="date"
                    value={value.to ?? ''}
                    min={value.from}
                    max={value.from ? minIso(addDaysIso(value.from, MAX_RANGE_DAYS), today) : today}
                    onChange={event => setTo(event.target.value)}
                    className="h-8 w-[10.5rem] px-2 text-[12px] font-normal normal-case tracking-normal"
                    aria-label="To date"
                  />
                </label>
                <span className="ml-auto self-center text-[11px] text-[color:var(--muted-foreground)]">
                  Up to {MAX_RANGE_DAYS} days
                </span>
                <Button size="sm" onClick={() => setOpen(false)} disabled={!value.from}>
                  Done
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </span>
    </div>
  )
}

function toIso(date: Date): string {
  // The calendar hands back local-midnight Dates; read the calendar fields
  // rather than formatting through a zone so the picked day never shifts.
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function minIso(a: string, b: string): string {
  return a < b ? a : b
}

function rangeLabel(from?: string, to?: string): string {
  if (!from) return 'Pick dates'
  const start = parseIsoDate(from)
  const end = to ? parseIsoDate(to) : null
  if (!start) return 'Pick dates'
  const sameYear = end ? start.getFullYear() === end.getFullYear() : true
  const startText = formatIST(start, sameYear ? 'd MMM' : 'd MMM yy')
  if (!end) return `${startText} →`
  return `${startText} – ${formatIST(end, 'd MMM yy')}`
}
